import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { IS_AUTHENTICATED_KEY } from '~/modules/auth/constants.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import type { RootState } from '~/store/store.ts';
import {
  selectGuaranteesManagerSupervisorInfoStatus,
  selectGuaranteesUakdoSupervisorInfoStatus,
  selectHasGuaranteesManagerAccess,
  selectHasGuaranteesUakdoControllerAccess,
  selectHasGuaranteesUakdoResponsibleAccess,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesManagerSupervisorInfo,
  fetchGuaranteesUakdoSupervisorInfo,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import {
  isGuaranteesAdministratorRoute,
  isGuaranteesUakdoApplicationStep,
  isGuaranteesUakdoControllerApplicationStep,
  isGuaranteesUdbtfApplicationStep,
  isGuaranteesUsapokApplicationStep,
  resolveGuaranteesSignInPath,
} from './routeUtils.ts';

const ProtectedGuaranteeRoute = () => {
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const { token, loading, isInitialized } = useSelector((state: RootState) => state.users);
  const managerStatus = useSelector(selectGuaranteesManagerSupervisorInfoStatus);
  const uakdoStatus = useSelector(selectGuaranteesUakdoSupervisorInfoStatus);
  const hasManagerAccess = useSelector(selectHasGuaranteesManagerAccess);
  const hasUakdoResponsibleAccess = useSelector(selectHasGuaranteesUakdoResponsibleAccess);
  const hasUakdoControllerAccess = useSelector(selectHasGuaranteesUakdoControllerAccess);
  const isAdministratorRoute = isGuaranteesAdministratorRoute(pathname);
  const isUakdoRoute = isGuaranteesUakdoApplicationStep(pathname);
  const isUakdoControllerRoute = isGuaranteesUakdoControllerApplicationStep(pathname);
  const requiresManagerAccess =
    isAdministratorRoute &&
    !isGuaranteesUdbtfApplicationStep(pathname) &&
    !isUakdoRoute &&
    !isUakdoControllerRoute &&
    !isGuaranteesUsapokApplicationStep(pathname);

  useEffect(() => {
    if (token && requiresManagerAccess && managerStatus === GuaranteesFetchStatus.Idle) {
      void dispatch(fetchGuaranteesManagerSupervisorInfo());
    }
  }, [dispatch, managerStatus, requiresManagerAccess, token]);

  useEffect(() => {
    if (
      token &&
      (isUakdoRoute || isUakdoControllerRoute) &&
      uakdoStatus === GuaranteesFetchStatus.Idle
    ) {
      void dispatch(fetchGuaranteesUakdoSupervisorInfo());
    }
  }, [dispatch, isUakdoControllerRoute, isUakdoRoute, token, uakdoStatus]);

  const isLoadingAccess =
    (requiresManagerAccess &&
      (managerStatus === GuaranteesFetchStatus.Idle ||
        managerStatus === GuaranteesFetchStatus.Loading)) ||
    ((isUakdoRoute || isUakdoControllerRoute) &&
      (uakdoStatus === GuaranteesFetchStatus.Idle ||
        uakdoStatus === GuaranteesFetchStatus.Loading));
  const isAuthorized = localStorage.getItem(IS_AUTHENTICATED_KEY);

  if ((!isInitialized || (loading && !token)) && isAuthorized) return <FullPageLoader />;
  if (!token) {
    return (
      <Navigate
        to={resolveGuaranteesSignInPath(pathname)}
        state={{ from: `${pathname}${search}` }}
        replace
      />
    );
  }
  if (isLoadingAccess) return <FullPageLoader />;
  if (
    (requiresManagerAccess && !hasManagerAccess) ||
    (isUakdoRoute && !hasUakdoResponsibleAccess) ||
    (isUakdoControllerRoute && !hasUakdoControllerAccess)
  ) {
    return (
      <Navigate
        to={resolveGuaranteesSignInPath(pathname)}
        state={{ from: `${pathname}${search}` }}
        replace
      />
    );
  }

  return <Outlet />;
};

export default ProtectedGuaranteeRoute;
